import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES, MAX_PAGE_SIZE } from "@miplatform/constants";
import { useComponentState, useMaps } from "@miplatform/hooks";

/**
 * Maps an array of region data to an object containing unique cities and counties
 * for each region code.
 *
 * @param {Array<{ regionCode: string, cityName: string, countyName: string }>} array -
 * An array of objects representing regions, each containing a region code, city name,
 *and county name.
 * @returns {{ cities: Object<string, string[]>, counties: Object<string, string[]> }}
 * An object with two properties:
 * - cities: An object where keys are region codes and values are arrays of unique city names.
 * - counties: An object where keys are region codes and values are arrays of unique county names.
 */
const mapRegionToCitiesAndCounties = (array) =>
  array.reduce(
    (acc, item) => {
      const { regionCode, cityName, countyName } = item;

      if (!acc.cities[regionCode]) {
        acc.cities[regionCode] = [];
        acc.counties[regionCode] = [];
      }

      if (!acc.cities[regionCode].includes(cityName)) {
        acc.cities[regionCode].push(cityName);
      }

      if (!acc.counties[regionCode].includes(countyName)) {
        acc.counties[regionCode].push(countyName);
      }

      return acc;
    },
    { cities: {}, counties: {} }
  );

/**
 * Custom hook to manage city data and interactions within the application.
 *
 * This hook provides functionality to fetch all cities, add new cities,
 * and manage the loading state of city data. It uses a component state
 * manager for handling asynchronous operations.
 *
 * @returns {Object} The values and functions for managing city data.
 * @property {Object} cities - An object mapping state codes to arrays of city names.
 * @property {boolean} isLoading - Indicates whether city data is currently being loaded.
 * @property {Function} getCounty - Function to fetch the county.
 * @property {Function} getCities - Function to fetch all cities and update the state.
 * @property {Function} addCity - Function to add a new city to the database.
 * @property {Function} addCityToState - Function to add a city directly to the local state.
 */
export const useCityViewModel = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const { getCounty } = useMaps();
  const [cities, setCities] = useState({});
  const [allCities, setAllCities] = useState([]);
  const [counties, setCounties] = useState({});

  /**
   * Fetches all cities from the API and updates the local state.
   *
   * @returns {Promise<void>} A promise that resolves when cities are fetched and set.
   */
  const getCities = () =>
    stateManager.run(async () => {
      const response = await homeService.cities.getAll({
        top: MAX_PAGE_SIZE,
      });
      if (response.status === HTTP_STATUSES.OK) {
        setAllCities(response.data);
        const { cities, counties } = mapRegionToCitiesAndCounties(
          response.data
        );
        setCities(cities);
        setCounties(counties);
      }
    });

  /**
   * Adds a city to the local state for a specific state.
   *
   * @param {string} city - The name of the city to add.
   * @param {string} state - The state code where the city is located.
   */
  const addCityToState = (city, state) => {
    setCities((prev) => ({
      ...prev,
      [state]: [...(prev[state] || []), city],
    }));
  };

  /**
   * Adds a new city to the database and the local state.
   * If the county is not provided, it fetches the county using the city and state.
   *
   * @param {Object} values - The values for the city to be added.
   * @param {string} values.city - The name of the city to add.
   * @param {string} values.regionCode - The state code where the city is located.
   * @param {string} [values.county] - The name of the county (optional).
   * @param {string} [values.timezone] - The timezone of the city (optional).
   * @param {boolean} [values.isValid=true] - Indicates if the city is valid (default is true).
   * @param {string} [values.countryCode="USA"] - The country code (default is "USA").
   * @returns {Promise<void>} A promise that resolves when the city is added.
   */
  const addCity = async (values = {}) => {
    const {
      city,
      regionCode,
      county,
      timezone,
      isValid = true,
      countryCode = "USA",
    } = values;
    let countyName = county;
    try {
      if (!county) {
        countyName = (await getCounty(city, regionCode)) || city;
      }
    } catch {
      countyName = city;
    }

    stateManager.run(() =>
      homeService.cities.create({
        data: {
          regionCode,
          countyName,
          cityName: city,
          isValid,
          countryCode,
          timezone,
        },
      })
    );
  };

  const updateCity = async (values = {}) => {
    const { id, modifiedOn, timezone } = values;

    stateManager.run(() =>
      homeService.cities.update({
        id,
        modifiedOn,
        data: {
          timezone,
        },
      })
    );
  };

  return {
    cities,
    counties,
    isLoading,
    allCities,
    getCities,
    getCounty,
    addCity,
    updateCity,
    addCityToState,
  };
};
